import {getISOWeek, startOfWeek, endOfWeek, eachDayOfInterval, isWithinInterval, differenceInCalendarDays} from 'date-fns';

import { DateUtil } from './DateUtil';

export const CalendarUtil = {

    // --------------------------------------------------------------------------------------------
    // ISO Week
    // --------------------------------------------------------------------------------------------

    /**
     * Get detailed information about the ISO week for the given reference date.
     * If no reference date is given, the current date is used.
     */
    isoWeek(refDate = new Date()) {
        const currentDate = new Date();
        const localStart = startOfWeek(refDate, { weekStartsOn: 1 })

        const nr    = getISOWeek(refDate)
        // ensure start, end and days are all in UTC (eliminate time zone issue)
        const start = new Date(Date.UTC(localStart.getFullYear(), localStart.getMonth(), localStart.getDate()));
        const end   = new Date(Date.UTC(
            endOfWeek(start, { weekStartsOn: 1 }).getFullYear(),
            endOfWeek(start, { weekStartsOn: 1 }).getMonth(),
            endOfWeek(start, { weekStartsOn: 1 }).getDate()
        ));  // Ensure end is also in UTC
        const days = eachDayOfInterval({ start, end }).map(d =>
            new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
        );

        // const prevStart = addDays(start, -7);
        // const nextStart = addDays(start, 7);
        const prevStart = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() - 7));
        const nextStart = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 7));

        const daysLabel = days.map(DateUtil.convertDateToString);
        const daysDayLabel = daysLabel.map(DateUtil.convertStringToDay);

        // Determine if refDate is within the current ISO week
        const isCurrentWeek = isWithinInterval(currentDate, { start, end });
        // Compute index (0 = Monday, 6 = Sunday) if in the current week
        const initialDayIdx = isCurrentWeek ? differenceInCalendarDays(currentDate, start) : 0;

        return {
            'nr'        : nr,
            'start'     : start,
            'end'       : end,
            'days'      : days,
            'prevStart' : prevStart,
            'nextStart' : nextStart,

            'startLabel'    : DateUtil.convertDateToString(start),
            'endLabel'      : DateUtil.convertDateToString(end),
            'daysLabel'     : daysLabel,
            'daysDayLabel'  : daysDayLabel,
            'prevStartLabel': DateUtil.convertDateToString(prevStart),
            'nextStartLabel': DateUtil.convertDateToString(nextStart),

            'startIso'      : DateUtil.convertDateToIsoString(start),
            'endIso'        : DateUtil.convertDateToIsoString(end),
            'daysIso'       : days.map(DateUtil.convertDateToIsoString),
            'prevStartIso'  : DateUtil.convertDateToIsoString(prevStart),
            'nextStartIso'  : DateUtil.convertDateToIsoString(nextStart),

            'isCurrentWeek' : isCurrentWeek,
            'initialDayIdx' : initialDayIdx
        }
    },

    /**
     * Get detailed information about the PREVIOUS ISO week of the given reference date.
     */
    isoWeekPrev(refDate = new Date()) {
        const current = CalendarUtil.isoWeek(refDate);
        return CalendarUtil.isoWeek(current.prevStart);
    },

    /**
     * Get detailed information about the NEXT ISO week of the given reference date.
     */
    isoWeekNext(refDate = new Date()) {
        const current = CalendarUtil.isoWeek(refDate);
        return CalendarUtil.isoWeek(current.nextStart);
    },

    // --------------------------------------------------------------------------------------------
    // Day Name and Label
    // --------------------------------------------------------------------------------------------

    /**
     * Return name of day in german for the given index (starting with 0 for monday).
     * If the index is out of range 0..6, the index value is returned instead.
     */
    dayName(dayIndex) {
        const dayNames = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
        return dayNames[dayIndex] ?? dayIndex; // Return index if no name is found
    }

};

